/**
 * useFinanceiroMeiosPagamento - Hook CRUD para meios de pagamento
 * 
 * Operações: listagem, criação, edição e soft delete
 * Tabela: financeiro_meios_pagamento
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação do hook
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('FinanceiroMeiosPagamento');

// Tipos de pagamento disponíveis
export const TIPOS_PAGAMENTO = [
  { value: 'pix', label: 'PIX' },
  { value: 'indefinido', label: 'Pergunte ao Cliente' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'carne', label: 'Carnê' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'doc', label: 'DOC' },
  { value: 'ted', label: 'TED' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'nota_promissoria', label: 'Nota Promissória' },
  { value: 'outros', label: 'Outros' },
] as const;

export type TipoPagamento = typeof TIPOS_PAGAMENTO[number]['value'];

// Interface do meio de pagamento
export interface FinanceiroMeioPagamento {
  id: string;
  tenant_id: string;
  nome: string;
  tipo_pagamento: TipoPagamento;
  conta_corrente_id: string | null;
  instrucoes: string | null;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Interface para criação/edição
export interface MeioPagamentoFormData {
  nome: string;
  tipo_pagamento: TipoPagamento;
  conta_corrente_id?: string | null;
  instrucoes?: string;
  is_active?: boolean;
}

/**
 * Hook para CRUD de meios de pagamento financeiros
 */
export function useFinanceiroMeiosPagamento() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Listagem de meios de pagamento ativos
  const { data: meios = [], isLoading, error } = useQuery({
    queryKey: ['financeiro-meios-pagamento', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      // SELECT restrito a campos necessários — sem expor metadata, tenant_id
      const { data, error } = await supabase
        .from('financeiro_meios_pagamento')
        .select('id, nome, tipo_pagamento, conta_corrente_id, instrucoes, is_active, created_at, updated_at')
        .eq('tenant_id', tenantId)
        .order('nome');

      if (error) {
        logger.error('Erro ao buscar meios de pagamento', { error: error.message });
        throw error;
      }

      return data as FinanceiroMeioPagamento[];
    },
    enabled: !!tenantId,
  });

  // Criação de meio de pagamento
  const createMeio = useMutation({
    mutationFn: async (formData: MeioPagamentoFormData) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      const { data, error } = await supabase
        .from('financeiro_meios_pagamento')
        .insert({
          tenant_id: tenantId,
          ...formData,
        })
        .select()
        .single();

      if (error) {
        logger.error('Erro ao criar meio de pagamento', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-meios-pagamento'] });
      toast.success('Meio de pagamento criado com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar meio de pagamento: ${error.message}`);
    },
  });

  // Edição
  const updateMeio = useMutation({
    mutationFn: async ({ id, ...formData }: MeioPagamentoFormData & { id: string }) => {
      const { data, error } = await supabase
        .from('financeiro_meios_pagamento')
        .update(formData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao atualizar meio de pagamento', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-meios-pagamento'] });
      toast.success('Meio de pagamento atualizado com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar meio de pagamento: ${error.message}`);
    },
  });

  // Soft delete
  const deleteMeio = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('financeiro_meios_pagamento')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        logger.error('Erro ao excluir meio de pagamento', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-meios-pagamento'] });
      toast.success('Meio de pagamento excluído com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir meio de pagamento: ${error.message}`);
    },
  });

  return {
    meios,
    isLoading,
    error,
    createMeio,
    updateMeio,
    deleteMeio,
  };
}
